import React from "react";
import { Phone, Mail, MapPin, Clock, Star, ExternalLink, CheckCircle2, MessageCircle, CalendarClock, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n";
import { CONTACT, FAQ } from "../data/content";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/fx/Reveal";
import { GlowCard } from "../components/fx/GlowCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const Consultation = () => {
  const { lang, ui } = useLang();
  const perks = {
    ro: [
      "Evaluare tehnică la fața locului",
      "Deviz clar și transparent, fără costuri ascunse",
      "Materiale certificate și soluții pe termen lung",
      "Garanție și suport tehnic după finalizare",
    ],
    en: [
      "On-site technical assessment",
      "Clear, transparent quote with no hidden costs",
      "Certified materials and long-term solutions",
      "Warranty and technical support after completion",
    ],
  };

  return (
    <GlowCard className="flex h-full flex-col p-8 md:p-10" data-testid="consultation-card">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-glow-2/25 bg-glow-2/10 text-glow-2">
          <CalendarClock className="h-6 w-6" />
        </div>
        <h3 className="font-display text-2xl font-bold text-white">
          {lang === "ro" ? "Programează o consultanță gratuită" : "Book a free consultation"}
        </h3>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-white/60">
        {lang === "ro"
          ? "Spune-ne pe scurt despre proiectul tău. Revenim rapid cu o soluție tehnică și un deviz clar."
          : "Tell us briefly about your project. We'll get back quickly with a technical solution and a clear quote."}
      </p>

      <ul className="mt-8 space-y-4">
        {perks[lang].map((p, i) => (
          <li key={i} className="flex items-start gap-3" data-testid={`perk-${i}`}>
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-glow-2" />
            <span className="text-sm text-white/75">{p}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex items-center gap-2 rounded-xl border border-white/10 bg-ink/50 px-4 py-3">
        <ShieldCheck className="h-4 w-4 text-brand" />
        <span className="text-xs text-white/55">
          {lang === "ro" ? "Răspuns în maximum 24 de ore lucrătoare." : "Response within 24 business hours."}
        </span>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <a href={`tel:${CONTACT.phoneHref}`} data-testid="consult-call-btn" className="btn-glow inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand px-6 py-4 text-sm font-semibold text-ink transition-transform hover:scale-[1.02] glow-ring">
          <Phone className="h-4 w-4" /> {ui.common.callNow}
        </a>
        <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="consult-whatsapp-btn" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-4 text-sm font-semibold text-white transition-colors hover:border-glow-2/50 hover:bg-white/5">
          <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp
        </a>
      </div>
    </GlowCard>
  );
};

export default function Contact() {
  const { lang, L, ui } = useLang();

  const details = [
    { icon: Phone, label: lang === "ro" ? "Telefon" : "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phoneHref}`, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
    { icon: MapPin, label: lang === "ro" ? "Adresă" : "Address", value: L(CONTACT.address), testid: "contact-address" },
    { icon: Clock, label: lang === "ro" ? "Program" : "Hours", value: L(CONTACT.hours), testid: "contact-hours" },
  ];

  return (
    <>
      <PageHero
        label={ui.nav.contact}
        title={lang === "ro" ? "Hai să construim ceva sigur și inteligent." : "Let's build something safe and intelligent."}
        desc={lang === "ro" ? "Contactează echipa VOLTPDR pentru consultanță, ofertă sau intervenții." : "Reach the VOLTPDR team for consulting, quotes or interventions."}
      />

      <section className="py-20 md:py-28">
        <div className="container-pdr grid gap-8 lg:grid-cols-2">
          {/* Contact details + review card */}
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              {details.map((d, i) => {
                const I = d.icon;
                const inner = (
                  <>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-glow-2/25 bg-glow-2/10 text-glow-2">
                      <I className="h-5 w-5" />
                    </div>
                    <p className="mt-4 font-mono text-xs uppercase tracking-widest text-white/40">{d.label}</p>
                    <p className="mt-1 font-medium text-white">{d.value}</p>
                  </>
                );
                return (
                  <Reveal key={i} delay={i * 0.05}>
                    {d.href ? (
                      <a href={d.href} data-testid={d.testid} className="block h-full rounded-2xl border border-white/10 bg-surface2/60 p-6 transition-colors hover:border-glow-2/40">{inner}</a>
                    ) : (
                      <div data-testid={d.testid} className="h-full rounded-2xl border border-white/10 bg-surface2/60 p-6">{inner}</div>
                    )}
                  </Reveal>
                );
              })}
            </div>

            {/* Google Business review card */}
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-surface2 to-ink p-7" data-testid="google-review-card">
                <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-deep/25 blur-3xl" />
                <div className="relative flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-white/50">Google Business</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="font-display text-4xl font-extrabold text-white">5.0</span>
                      <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-glow-2 text-glow-2" />)}</div>
                    </div>
                    <p className="mt-1 text-sm text-white/45">{lang === "ro" ? "Recenzii verificate de clienți" : "Verified client reviews"}</p>
                  </div>
                </div>
                <div className="relative mt-6 flex flex-wrap gap-3">
                  <a href={CONTACT.googleReview} target="_blank" rel="noreferrer" data-testid="see-reviews-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-glow-2/50">
                    {ui.common.seeAllReviews} <ExternalLink className="h-4 w-4" />
                  </a>
                  <a href={CONTACT.googleReview} target="_blank" rel="noreferrer" data-testid="leave-review-btn" className="btn-glow inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-ink glow-ring">
                    {ui.common.leaveReview}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Consultation */}
          <Reveal variant="scale"><Consultation /></Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24">
        <div className="container-pdr">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <iframe
                title="VOLTPDR location"
                src={CONTACT.mapEmbed}
                className="h-[380px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 bg-surface1/40 py-24 md:py-32">
        <div className="container-pdr grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading label="FAQ" title={lang === "ro" ? "Întrebări frecvente." : "Frequently asked questions."} />
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg font-semibold text-white hover:text-glow-2 hover:no-underline">
                    {L(f)?.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-white/60">{L(f)?.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
